import { useCallback, useEffect, useState } from 'react';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { computeOfflineBootstrapEvidence } from '../offlineBootstrapEvidence';

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `null` mientras evalúa snapshot + SQLite; luego indica si hay evidencia para no bloquear el arranque offline.
 */
export function useOfflineBootstrapEvidence(userId: string | undefined): {
  evidence: boolean | null;
  refresh: () => void;
} {
  const [value, setValue] = useState<boolean | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!userId) {
      setValue(null);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    let cancelled = false;

    void (async () => {
      // ponytail: 5 intentos bastan; 10 + backoff llegaba a ~5s solo en sleeps
      const maxAttempts = 5;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const ok = await computeOfflineBootstrapEvidence(userId);
          if (!cancelled) setValue(ok);
          return;
        } catch (e) {
          const retryable = isDatabaseUnavailableError(e) || attempt < 2;
          if (!cancelled && retryable && attempt < maxAttempts - 1) {
            await delay(80 * (attempt + 1));
            continue;
          }
          if (!cancelled) setValue(false);
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshTick, userId]);

  return { evidence: value, refresh };
}
